import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Upload, Plus, Download, Settings, FileText, Hash } from 'lucide-react';
import { api, getUserAssignedCourses, type AssignedCourse } from '../lib/api';
import { importSyllabusByTitle, importSyllabusBySortOrder } from '../lib/api/editor';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/common/LogoutButton';
import StudentPortalButton from '../components/StudentPortalButton';
import TokenBalance from '../components/TokenBalance';
import logo from '../assets/imgs/logo.png';

type Course = {
  id: number;
  title: string;
  description: string;
  status: string;
};

// 预置的模板内容
const SYLLABUS_TEMPLATE = `中小学AI课程大纲示例
课程说明：这是示例课程的描述...
________________________________________
【一阶段】感知AI
课次	主题	核心能力模型  知识点拆解	实战作品	解决痛点	学完马上能做	未来能做  承上启下
01	示例课时1	 审美与交互(C4)  图像识别	 卡片游戏	L1	体验	无	
02	示例课时2	 语音识别	AI能听声音	录音猜谜	L1	体验	01->02	
【二阶段】体验AI
03	示例课时3	简单逻辑	AI能思考	分类游戏	L1	理解	02->03	
`;

export default function DashboardPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [assignedCourses, setAssignedCourses] = useState<AssignedCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleImportRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const sortOrderImportRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const { user } = useAuth();

  const fetchCourses = async () => {
    console.log('=== DashboardPage fetchCourses 被调用 ===');
    console.log('当前用户:', user);
    if (!user) {
      console.log('用户为空，不加载课程');
      return;
    }
    
    setLoading(true);
    try {
      console.log('开始加载课程...');
      if (user.role === 'sys') {
        console.log('sys角色，加载所有课程');
        const res = await api.get('/courses/');
        console.log('获取到的课程:', res.data);
        setCourses(res.data);
        setAssignedCourses([]);
      } else {
        console.log('非sys角色，加载用户有权限的课程');
        const res = await getUserAssignedCourses(user.id);
        const assigned = res.data;
        console.log('用户权限数据:', assigned);
        setAssignedCourses(assigned);
        
        const courseIds = [...new Set(assigned.map(ac => ac.course_id))];
        console.log('有权限的课程ID:', courseIds);
        if (courseIds.length > 0) {
          const courseDetails = await Promise.all(
            courseIds.map(id => api.get(`/courses/${id}/`).catch(() => null))
          );
          const validCourses = courseDetails
            .filter(r => r !== null)
            .map(r => r.data);
          console.log('获取到的课程详情:', validCourses);
          setCourses(validCourses);
        } else {
          console.log('用户没有权限课程');
          setCourses([]);
        }
      }
    } catch (err) {
      console.error('加载课程失败:', err);
      setCourses([]);
    } finally {
      console.log('课程加载完成');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [user]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/courses/import/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('✅ Import Successful!');
      fetchCourses();
    } catch (err) {
      alert('❌ Import Failed');
      console.error(err);
    }
  };

  const handleTitleImport = async (courseId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await importSyllabusByTitle(courseId, file);
      alert('✅ 按标题匹配导入成功！');
      fetchCourses();
    } catch (err) {
      alert('❌ 按标题匹配导入失败');
      console.error(err);
    }
  };

  const handleSortOrderImport = async (courseId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await importSyllabusBySortOrder(courseId, file);
      alert('✅ 按ID匹配导入成功！');
      fetchCourses();
    } catch (err) {
      alert('❌ 按ID匹配导入失败');
      console.error(err);
    }
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([SYLLABUS_TEMPLATE], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'syllabus_template.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center h-14">
            <img src={logo} alt="Logo" className="h-full object-contain" />
          </div>
          <div className="flex gap-3 items-center">
            {/* Settings Button - Only visible to sys role */}
            {user?.role === 'sys' && (
              <Link 
                to="/admin"
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 hover:text-brand-600 transition-colors"
                title="Admin Console"
              >
                <Settings size={18} />
              </Link>
            )}

            {/* Download Template Button */}
            <button 
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 bg-white border border-gray-300 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 hover:text-brand-600 transition-colors"
              title="Download Syllabus Template"
            >
              <Download size={18} />
              Template
            </button>

            {/* Import Button */}
            <button 
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                  fileInputRef.current.click();
                }
              }}
              className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload size={18} />
              Import Syllabus
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept=".txt" 
              onChange={handleFileUpload}
            />
            
            {/* New Course Button */}
            <button className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors">
              <Plus size={18} />
              New Course
            </button>
            
            {/* Token Balance */}
            <TokenBalance />
            
            {/* Logout Button */}
            <LogoutButton />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}

        {/* Course Grid */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {courses.map(course => (
              <div 
                key={course.id} 
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-brand-300 transition-all p-6 group min-h-[300px] flex flex-col"
              >
                <Link 
                  to={`/course/${course.id}`}
                  className="flex-1 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center">
                      <BookOpen size={20} />
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${course.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {course.status}
                    </span>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 group-hover:text-brand-600 transition-colors">
                    {course.title}
                  </h3>
                  <div className="mt-2 text-sm text-gray-500 space-y-2">
                    {course.description ? (
                      course.description.split('\n').map((line, index) => (
                        <p key={index} className={line.includes('：') ? 'font-medium text-gray-700' : ''}>
                          {line}
                        </p>
                      ))
                    ) : (
                      <p>No description.</p>
                    )}
                  </div>
                </Link>
                
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = titleImportRefs.current[course.id];
                      if (input) {
                        input.value = '';
                        input.click();
                      }
                    }}
                    className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors"
                    title="按标题匹配导入大纲"
                  >
                    <FileText size={14} />
                    按标题导入
                  </button>
                  <input 
                    type="file" 
                    ref={el => titleImportRefs.current[course.id] = el}
                    className="hidden" 
                    accept=".txt" 
                    onChange={(e) => handleTitleImport(course.id, e)}
                  />
                  
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = sortOrderImportRefs.current[course.id];
                      if (input) {
                        input.value = '';
                        input.click();
                      }
                    }}
                    className="flex items-center gap-1 text-xs bg-purple-50 text-purple-600 px-3 py-1.5 rounded-lg hover:bg-purple-100 transition-colors"
                    title="按ID匹配导入大纲"
                  >
                    <Hash size={14} />
                    按ID导入
                  </button>
                  <input 
                    type="file" 
                    ref={el => sortOrderImportRefs.current[course.id] = el}
                    className="hidden" 
                    accept=".txt" 
                    onChange={(e) => handleSortOrderImport(course.id, e)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!loading && courses.length === 0 && (
          <div className="text-center py-20 text-gray-400 bg-white rounded-xl border border-dashed border-gray-300">
            <p>No courses found.</p>
            <p className="text-sm mt-2">You don't have permission to access any courses yet.</p>
          </div>
        )}
      </div>
      <StudentPortalButton />
    </div>
  );
}
